import { useEffect, useState } from "react";
import { Bell, BellRing, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "./LoadError";

const b64ToBytes = (v) => {
  const pad = "=".repeat((4 - (v.length % 4)) % 4);
  return Uint8Array.from(atob((v + pad).replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));
};
export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function PushToggle({ compact }) {
  const [state, setState] = useState("checking");
  const [devices, setDevices] = useState(0);

  useEffect(() => {
    api.get("/push/status").then((r) => setDevices(r.data.devices)).catch(() => {});
    if (!supported()) { setState(isStandalone() ? "unsupported" : "install"); return; }
    navigator.serviceWorker.getRegistration("/").then(async (reg) => {
      const sub = reg && (await reg.pushManager.getSubscription());
      setState(sub && Notification.permission === "granted" ? "on" : Notification.permission === "denied" ? "denied" : "off");
    }).catch(() => setState("off"));
  }, []);

  const enable = async () => {
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState(perm === "denied" ? "denied" : "off"); return toast.error("Notifications were not allowed"); }
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/public-key");
      const sub = (await reg.pushManager.getSubscription()) || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.key) });
      const r = await api.post("/push/subscriptions", { ...sub.toJSON(), device: navigator.userAgent.slice(0, 120) });
      setDevices(r.data.devices); setState("on"); toast.success("iPhone alerts switched on");
    } catch (e) { toast.error(errMsg(e, "Could not switch on alerts")); }
  };
  const test = async () => {
    try { const { data } = await api.post("/push/test"); data.sent ? toast.success(`Test alert sent to ${data.sent} device${data.sent > 1 ? "s" : ""}`) : toast.error("No device received it — switch alerts on first"); }
    catch (e) { toast.error(errMsg(e, "Test failed")); }
  };

  if (compact && (state === "on" || state === "checking")) return null;
  const msg = {
    install: "On your iPhone: open this page in Safari, tap Share → Add to Home Screen, then open the CRM from the new icon and switch alerts on here.",
    unsupported: "This device doesn't support notifications. iPhone needs iOS 16.4 or later.",
    denied: "Notifications are blocked. Turn them on in iPhone Settings → Notifications → EC CRM.",
    off: "Get an alert on this phone the moment a customer replies, with a link straight to their record.",
    on: `Alerts are on for this device (${devices} device${devices === 1 ? "" : "s"} registered).`,
    checking: "Checking…",
  }[state];
  return (
    <div className={`bg-[#0F2E23] rounded-xl border ${compact ? "border-brass/30" : "border-white/10"} p-4 ${compact ? "mb-4" : "mb-5"}`} data-testid="push-toggle">
      <div className="flex items-center gap-2 mb-1.5">{state === "on" ? <BellRing className="w-4 h-4 text-emerald-300" /> : state === "install" ? <Smartphone className="w-4 h-4 text-brass" /> : <Bell className="w-4 h-4 text-brass" />}
        <span className="text-sm font-semibold text-white">iPhone reply alerts</span></div>
      <p className="text-xs text-emerald-100/70" data-testid="push-state-msg">{msg}</p>
      <div className="flex gap-2 mt-3">
        {state === "off" && <button onClick={enable} data-testid="push-enable" className="bg-brass text-emerald-950 font-semibold rounded-lg px-4 h-10 text-sm">Switch on alerts</button>}
        {(state === "on" || (!compact && devices > 0)) && <button onClick={test} data-testid="push-test" className="bg-white/[0.06] text-white rounded-lg px-4 h-10 text-sm">Send test alert</button>}
      </div>
    </div>
  );
}
